import { AccountRole, Agent, AgentBase, Animal, AnimalDeleteReason, ImageReference, InteractivePersona, PracticeType, Topic } from ".";
import type { TestUser } from "./TestResources";
import type { WholesalerInfo } from ".";

export interface GetAzureOpenAITokenRequest {
  useGpt41?: boolean;
}

export interface GetAzureOpenAITokenResponse {
  token: string;
  endpoint: string;
  expiresOn: number;
}

export interface GetRemainingTopicsRequest {
  profileId: string;
  topics: Topic[];
}

export interface GetRemainingTopicsResponse {
  topicIndexes?: number[];
  reasoning?: string[];
}

export interface AddSharedAnimalRequest {
  animalId: string;
  accountId: string;
  mindId?: string;
}

export interface AppointAmbassadorRequest {
  accountId: string;
  animalId: string;
  personaId: string;
  replace?: boolean;
  /** Champion's public face — defaults to authenticated User agent */
  from?: Agent;
  /** Role config consumed by AddAnimalWithJobPage */
  roles?: Animal["roles"];
  /** Account-local Support doc id copied to public on publish; stored on persona */
  introSupportId?: string;
}

export interface AppointAmbassadorResponse {
  publicAnimalId: string;
  shareUrl: string;
}

export interface RevokeAmbassadorRequest {
  accountId: string;
  animalId: string;
}

export interface RevokeAmbassadorResponse {
  revoked: boolean;
}

export interface AnalyzeDocumentRequest {
  accountId: string;
  documentId: string;
  userPrompt?: string; // Optional additional context from user
}

export interface AnalyzeDocumentResponse {
  success: boolean;
  message: string;
}

export interface GenerateInsightsSummaryRequest {
  accountId: string;
  documentIds: string[]; // Array of document IDs to analyze
  mindId?: string; // Optional Mind ID to save insights to
}

export type GenerateInsightsSummaryResponse = {
  success: boolean;
  message: string;
  summary: {
    insights: {
      title: string;
      description: string;
    }[];
    overallTheme: string;
  };
};

export type ChangeUserRoleRequest =
  | {
      action: "add" | "update";
      accountId: string;
      inviteEmail: string;
      role: AccountRole;
      mindId?: string;
      greeterAnimalId?: string;
      parentConsent?: string;
    }
  | {
      action: "remove" | "resend";
      accountId: string;
      inviteEmail: string;
    }
  | {
      action: "get" | "accept";
      inviteCode: string;
    };

export interface InviteDetails {
  invitedBy?: AgentBase;
  greeter?: AgentBase;
  accountId: string;
  accountName: string;
  accountImage?: ImageReference;
}

// Create a mapping type from action to response type
export type ChangeUserRoleResponseMap = {
  add: {
    inviteCode: string;
  };
  remove: void;
  update: void;
  resend: {
    inviteCode: string;
  };
  get: InviteDetails;
  accept: void;
};

export type ChangeUserRoleResponse = ChangeUserRoleResponseMap[keyof ChangeUserRoleResponseMap];

export type UploadImageRequest = {
  accountId: string;
  isGenerated: boolean;
  prompt?: string;
  id?: string;
} & (
  | {
      base64Data: string;
    }
  | {
      url: string;
    }
);

export interface UploadImageResponse {
  image: ImageReference;
}

export function getUploadImageBasePath(accountId: string, imageId: string) {
  return `Account/${accountId}/images/${imageId}/upload`;
}

export function getUploadImageOriginalPath(accountId: string, imageId: string) {
  return `${getUploadImageBasePath(accountId, imageId)}-original`;
}

export type ManageJobRequest = (
  | {
      type: "create";
      handlerName: string;
      mindId?: string;
      animalId?: string;
      paused?: boolean;
      jobId?: string;
    }
  | {
      type: "update";
      jobId: string;
    }
  | {
      type: "action";
      jobId: string;
      actionId: string;
    }
  | {
      type: "next";
      jobId: string;
      triggerTime?: number; // milliseconds since epoch
    }
) & {
  accountId: string;
  timezone?: string;
  input?: Record<string, any>;
  prompt?: string;
  preview?: boolean;
};

export type ManageJobResponse = JobAsyncResult | JobInputProposal;

export interface JobAsyncResult {
  jobId: string;
  handleJobTaskId?: string;
}

export interface JobInputProposal extends JobAgentProposal {
  input?: Record<string, any>;
  /** Storyteller preview: one friendly sentence (no jargon) describing how the story will feel. */
  storyTellerPreviewSummary?: string;
  /** Storyteller preview: optional note when some requests apply when the next story series begins. */
  storyTellerPreviewNote?: string;
  /** Daily Boosts preview: one friendly sentence before the user confirms an edit. */
  dailyBoostsPreviewSummary?: string;
  /** Routine preview: one friendly sentence confirming the request was heard (many routine edits have no visible row). */
  routineChecklistPreviewSummary?: string;
  /** Daily Boosts preview: optional note (e.g. when delivery time is changing). */
  dailyBoostsPreviewNote?: string;
}

export interface JobAgentProposal {
  agent?: {
    name: string;
    image?: ImageReference;
    animalId?: string;
  };
  promoteToRole?: string;
  reason?: string;
}

export interface ChatIntent {
  transparentImages?: boolean;
  alternateImages?: AlternateImagesIntentInput;
  produceScript?: boolean;
}

export interface AlternateImagesIntentInput {
  tokenLetter: string;
  dimensions: number;
}

export interface ChatModifier {
  removeImages?: boolean;
  dontSave?: boolean;
  updatePersona?: boolean;
  translateThoughts?: boolean;
  imageAspectRatio?: "1:1" | "2:3" | "3:2" | "3:4" | "4:3" | "4:5" | "5:4" | "9:16" | "16:9" | "21:9";
}

export type ChatRequest = {
  accountId: string;
  promptName?: string;
  intents?: ChatIntent;
  modifiers?: ChatModifier;
} & (
  | {
      animalId?: string;
      personaId?: string;
      mindId?: string;
      message?: string;
      imageIds?: string[];
      baselineSessionId?: string;
    }
  | {
      sessionId?: string;
      message: string;
      imageIds?: string[];
    }
);

export interface ChatResponse {
  sessionId: string;
  message: string;
  thoughts?: string | string[];
  images?: ImageReference[];
}

export type ChatChunk =
  | { event: string; data?: any }
  | {
      status?: string;
      ideas?: string[];
      imageVariation?: string;
    }
  | {
      image: {
        id: string;
        large: string;
        small: string;
        md5Hash: string;
      };
      isContent: boolean;
    }
  | {
      thought: string;
    }
  | {
      text: string;
    };

// Fun Facts for image generation loading states
export type FunFactsVariant = "default" | "personality-wonder";

export interface FunFactsRequest {
  description?: string;
  imageBase64?: string | null;
  characterName?: string;
  variant?: FunFactsVariant;
}

export interface FunFactsResponse {
  success: boolean;
  facts: string[];
  error?: string;
}

export interface ApplyPersonalityHintsRequest {
  accountId: string;
  characterId: string;
  personaId: string;
}

export interface ApplyPersonalityHintsResponse {
  success: boolean;
  applied?: boolean;
  error?: string;
}

// Character deletion — pauses the character's jobs before soft-deleting so
// they can be reassigned rather than orphaned (see backend deleteCharacter).
export interface DeleteCharacterRequest {
  accountId: string;
  animalId: string;
  /**
   * Why the character is being deleted, stored as `Animal.deletedReason`.
   * Only "user-retired" characters appear in the restorable Retired
   * Characters list; "reported" removals (and legacy soft-deletes with no
   * reason) stay hidden for good. Defaults to "user-retired".
   */
  reason?: AnimalDeleteReason;
  /**
   * The mind retiring the character (the user's browsing context). When the
   * character is also present on OTHER minds (crew rows or Animal.mind),
   * the backend performs a PARTIAL retire scoped to this mind — crew-row
   * removal + this mind's jobs paused + unassignment when this is the
   * assigned mind — leaving the character alive for the other minds. When
   * this is the character's only mind (or mindId is omitted) the backend
   * does a full retire (soft-delete + retired list).
   */
  mindId?: string;
}

export interface DeleteCharacterResponse {
  animalId: string;
  /**
   * Jobs marked with the character-deleted pause reason: active/review jobs
   * newly paused, plus already-paused jobs re-marked so automation (e.g.
   * subscription auto-resume) can never run them as the deleted character.
   * Scoped to the retiring mind on partial retires.
   */
  pausedJobIds: string[];
  /**
   * True when the character was the captain (defaultAnimal) — of the
   * retiring mind when `mindId` was given, otherwise of `Animal.mind`.
   */
  wasCaptain: boolean;
  /**
   * False when the retire was PARTIAL (the character remains alive on other
   * minds); true when the character was soft-deleted account-wide.
   */
  fullyRetired: boolean;
}

// Per-job character reassignment (see backend reassignJobAgent).
export interface ReassignJobAgentRequest {
  accountId: string;
  jobId: string;
  newAnimalId: string;
  /** Required true for storyTeller jobs — ends the current story and restarts planning. */
  startNewStoryArc?: boolean;
}

export interface ReassignJobAgentResponse {
  jobId: string;
  newAnimalId: string;
  startedNewStoryArc: boolean;
}

// Personality questions generated during character creation
export interface PersonalityQuestionOption {
  id: string;
  label: string;
  emoji: string;
}

export interface PersonalityQuestion {
  id: string;
  prompt: string;
  emoji?: string;
  options: PersonalityQuestionOption[];
}

export interface PersonalityQuestionsRequest {
  description?: string | null;
  imageBase64?: string | null;
  characterName?: string | null;
  count?: number | null;
  excludePrompts?: string[] | null;
}

export interface PersonalityQuestionsResponse {
  success: boolean;
  questions: PersonalityQuestion[];
  error?: string;
}

// Clinician callable: get (no auth), update, add, getReferrals (auth + account access)
// image is the small URL with fallback to large when returned from get
export interface ClinicianInfoPublic extends ClinicianInfoPublicCommon {
  image?: ImageReference;
  messageFromImage?: ImageReference;
  guidePersona?: InteractivePersona;
}

export interface ClinicianInfoPublicCommon {
  name: string;
  clinicianType: string;
  practiceType: PracticeType;
  message: string;
  messageFrom: string;
  code: string;
  testResourceGroup?: string;
}
export interface ClinicianInfoPublicWritable extends ClinicianInfoPublicCommon {
  imageId?: string;
  messageFromImageId?: string;
  guideAnimalId?: string | null;
}

export type ClinicianRequest =
  | { action: "get"; code: string }
  | ({ action: "update"; accountId: string; email?: string } & Partial<ClinicianInfoPublicWritable>)
  | ({ action: "add"; email?: string; attribution?: Record<string, any> } & ClinicianInfoPublicWritable)
  | { action: "getReferrals"; accountId: string };

export type ClinicianResponseMap = {
  get: ClinicianInfoPublic;
  update: void;
  add: { accountId: string };
  getReferrals: { count: number };
};

export type ClinicianResponse = ClinicianResponseMap[keyof ClinicianResponseMap];

// TestManager callable: getUsers (no auth), addUser, resetUser, deleteUser (auth: createdBy or admin)
export type TestManagerRequest =
  | { action: "getUsers"; resourceGroupId: string }
  | {
      action: "addUser";
      resourceGroupId: string;
      name?: string;
      description?: string;
      email?: string;
      pictureUrl?: string;
    }
  | { action: "resetUser"; resourceGroupId: string; uid: string }
  | { action: "deleteUser"; resourceGroupId: string; uid: string }
  | { action: "becomeTestUser"; resourceGroupId: string };

export type TestManagerResponseMap = {
  getUsers: TestUser[];
  addUser: TestUser;
  resetUser: void;
  deleteUser: void;
  becomeTestUser: TestUser;
};

export type TestManagerResponse = TestManagerResponseMap[keyof TestManagerResponseMap];

/**
 * Single composite purchases callable. Consolidates wholesaler account management,
 * embedded Stripe Checkout, and in-app code redemption behind one `action`-discriminated request.
 *
 * - `addWholesaler` / `updateWholesaler`: admin-only create/update of a wholesaler account.
 *   `addWholesaler` may set an owner user (`ownerUserId`), which sets that user's
 *   `defaultWholesalerAccount` and account membership.
 * - `createCheckout`: create an embedded Stripe Checkout Session for a 1-year access purchase.
 * - `getCheckoutSession`: retrieve a Checkout Session's status for the return page.
 * - `redeem`: redeem an access code in the consumer app (transfers entitlement to the redeemer).
 *
 * Note: purchase listing is intentionally not exposed here — the admin and www apps read the
 * `Account/{id}/Purchase` documents directly from Firestore.
 */
export type PurchasesRequest =
  | ({ action: "addWholesaler"; name: string; email: string; ownerUserId?: string } & WholesalerInfo)
  | ({ action: "updateWholesaler"; accountId: string; name?: string; email?: string } & Partial<WholesalerInfo>)
  | {
      action: "createCheckout";
      accountId: string;
      /** Optional purchase metadata stored alongside the resulting Purchase doc. */
      clientName?: string;
      clientId?: string;
      caseManagerName?: string;
      caseManagerEmail?: string;
      /** Quantity of access codes to purchase (defaults to 1). */
      quantity?: number;
    }
  | { action: "getCheckoutSession"; sessionId: string }
  | { action: "inspectCode"; code: string }
  | { action: "redeem"; code: string; accountId: string };

export type PurchasesResponseMap = {
  addWholesaler: { accountId: string };
  updateWholesaler: void;
  createCheckout: {
    /** Client secret used to mount Stripe's embedded Checkout in the React app. */
    clientSecret: string;
    sessionId: string;
    /** Stripe publishable key for the active environment (safe to expose client-side). */
    publishableKey: string;
  };
  getCheckoutSession: {
    /** Checkout Session status: `complete`, `open`, or `expired`. */
    status: string | null;
    /** Payment status: `paid`, `unpaid`, or `no_payment_required`. */
    paymentStatus: string | null;
    /** Redemption code, present once the webhook has provisioned the purchase. */
    redemptionCode?: string;
  };
  /** Public (no auth): validate an access code and describe what it grants. */
  inspectCode: {
    /** `valid`: redeemable. `redeemed`: already used. `not_found`: unknown code. */
    status: "valid" | "redeemed" | "not_found";
    /** Length of access granted, in days. */
    durationDays?: number;
    /** Entitlement identifiers the code grants (UI maps these to display names). */
    entitlements?: string[];
  };
  redeem: {
    success: boolean;
    /** Entitlements granted to the redeeming account. */
    entitlements: string[];
    alreadyRedeemed?: boolean;
  };
};

export type PurchasesResponse = PurchasesResponseMap[keyof PurchasesResponseMap];

/**
 * Kick off the captain's free-access lifecycle messages (T−12h heads-up, T0
 * expired, T+24h comeback) for the account's RevenueCat free-access promo
 * grant. Called once onboarding completes (first routine created). Idempotent —
 * safe to call again; an existing lifecycle job short-circuits.
 */
export interface FreeAccessRequest {
  action: "start";
  accountId: string;
}

export interface FreeAccessResponse {
  success: boolean;
  /** True when a lifecycle job already existed for this account. */
  alreadyStarted?: boolean;
  /** True when no short free-access promo applies (no grant, waiver, subscribed…). */
  skipped?: boolean;
  /** ISO timestamp the free-access window ends (when a lifecycle was created/found). */
  freeAccessEndsAt?: string;
  jobId?: string;
}

export interface AddRaffleEntryRequest {
  name: string;
  email: string;
  campaignId: string;
  marketingOptIn: boolean;
  source: string;
}

export interface AddRaffleEntryResponse {
  success: boolean;
  message: string;
  id?: string;
}
